import { useState, useEffect } from 'react';
import { apiService } from '../../../../services/api';
import { useAutoSave } from '../../../../hooks/useAutoSave';
import { useToast } from '../../../../contexts/ToastContext';
import type { ParcelData } from '../../../map';

interface UseParcelsDataProps {
  projectId: string;
  areaId?: string;
  canEdit: boolean;
  isEditMode: boolean;
  selectedOwnerId?: string | null;
}

export const useParcelsData = ({ projectId, areaId, canEdit, isEditMode, selectedOwnerId }: UseParcelsDataProps) => {
  const { showSuccess, showError } = useToast();

  const [allParcels, setAllParcels] = useState<any[]>([]); // Alle Parcels (unabhängig von selectedOwnerId)
  const [filteredParcels, setFilteredParcels] = useState<any[]>([]); // Gefilterte Parcels (nach Owner)
  const [isLoadingParcels, setIsLoadingParcels] = useState(false);
  const [selectedParcelId, setSelectedParcelId] = useState<string | null>(null);
  const [selectedParcel, setSelectedParcel] = useState<ParcelData | null>(null);
  const [parcelNotizen, setParcelNotizen] = useState<string>('');

  // Lade alle Parcels einmalig - dient als Basis für allParcels und parcels
  const loadAllParcels = async () => {
    if (!projectId) return;

    try {
      setIsLoadingParcels(true);
      const parcelsData = await apiService.getParcels(projectId);
      setAllParcels(parcelsData);
    } catch (error: any) {
      if (error.message?.includes('404')) {
        setAllParcels([]);
      } else {
        setAllParcels([]);
      }
    } finally {
      setIsLoadingParcels(false);
    }
  };

  // Lade gefilterte Parcels für einen bestimmten Owner
  const loadOwnerParcels = async () => {
    if (!projectId || !areaId || !selectedOwnerId) return;

    try {
      setIsLoadingParcels(true);
      const parcelsData = await apiService.getOwnerParcels(projectId, areaId, selectedOwnerId);
      setFilteredParcels(parcelsData);
    } catch (error: any) {
      setFilteredParcels([]);
    } finally {
      setIsLoadingParcels(false);
    }
  };

  // parcels = gefilterte Ansicht (Owner-spezifisch) oder alle
  const parcels = selectedOwnerId ? filteredParcels : allParcels;

  const handleParcelClick = (parcelId: string | null) => {
    if (parcelId === null || parcelId === '') {
      setSelectedParcelId(null);
      setSelectedParcel(null);
      setParcelNotizen('');
      return;
    }
    const parcel = parcels.find((p) => p.id === parcelId) || null;
    setSelectedParcelId(parcelId);
    setSelectedParcel(parcel);
    setParcelNotizen(parcel?.notizen || '');
  };

  // Auto-Save Hook für Parcel-Notizen
  const {
    isSaving,
    hasUnsavedChanges,
    lastSaved,
  } = useAutoSave(
    parcelNotizen,
    {
      delay: 1000,
      enabled: canEdit && isEditMode && selectedParcelId !== null,
      onSave: async (notizen) => {
        if (!selectedParcelId) return;
        await apiService.updateParcelNotes(projectId, selectedParcelId, notizen);
        showSuccess('Gespeichert');
        // Parcels neu laden, um aktualisierte Daten zu erhalten
        await loadAllParcels();
      },
      onError: () => {
        showError('Speichern fehlgeschlagen');
      }
    }
  );

  // Lade alle Parcels beim Mount
  useEffect(() => {
    if (projectId && areaId) {
      loadAllParcels();
    }
  }, [projectId, areaId]);

  // Lade Owner-spezifische Parcels bei Auswahl eines Owners
  useEffect(() => {
    if (selectedOwnerId && projectId && areaId) {
      loadOwnerParcels();
    } else {
      // Kein Owner ausgewählt → Filter zurücksetzen
      setFilteredParcels([]);
    }
  }, [selectedOwnerId, projectId, areaId]);

  // Aktualisiere Notizen wenn sich selectedParcel ändert
  useEffect(() => {
    if (selectedParcel) {
      setParcelNotizen(selectedParcel.notizen || '');
    }
  }, [selectedParcel?.id]);

  return {
    parcels,
    allParcels, // Alle Parcels (unabhängig von selectedOwnerId)
    isLoadingParcels,
    selectedParcelId,
    selectedParcel,
    parcelNotizen,
    setParcelNotizen,
    loadParcels: loadAllParcels, // Alias für Abwärtskompatibilität (lädt alle Parcels neu)
    loadAllParcels,
    loadOwnerParcels,
    handleParcelClick,
  };
};

